import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import {
  Home, AlertTriangle, ClipboardList, Calendar, Megaphone,
  Users, Search, LogOut, Building2, Mic, Wrench, FolderOpen, User, Settings, MoreHorizontal, IndianRupee
} from "lucide-react";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: Home, roles: ["committee", "owner", "tenant"] },
  { to: "/issues", label: "Issues", icon: AlertTriangle, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/todos", label: "To-dos", icon: ClipboardList, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/meetings", label: "Meetings", icon: Calendar, roles: ["committee", "owner", "tenant"] },
  { to: "/notices", label: "Notices", icon: Megaphone, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/directory", label: "Directory", icon: Users, roles: ["committee", "owner", "tenant"] },
  { to: "/vendors", label: "Vendors", icon: Wrench, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/documents", label: "Documents", icon: FolderOpen, roles: ["committee", "owner", "tenant"] },
  { to: "/dues", label: "Dues", icon: IndianRupee, roles: ["committee", "owner", "tenant"], preview: true },
  { to: "/search", label: "Search", icon: Search, roles: ["committee", "owner", "tenant", "staff"] },
];

// Bottom-nav for mobile; non-primary "More" opens a sheet with rest of nav
const mobileItems = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/issues", label: "Issues", icon: AlertTriangle },
  { to: "/issues/new", label: "Report", icon: Mic, primary: true },
  { to: "/notices", label: "Notices", icon: Megaphone },
];

const moreItems = [
  { to: "/todos", label: "To-dos", icon: ClipboardList, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/meetings", label: "Meetings", icon: Calendar, roles: ["committee", "owner", "tenant"] },
  { to: "/directory", label: "Directory", icon: Users, roles: ["committee", "owner", "tenant"] },
  { to: "/vendors", label: "Vendors", icon: Wrench, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/documents", label: "Documents", icon: FolderOpen, roles: ["committee", "owner", "tenant"] },
  { to: "/dues", label: "Dues", icon: IndianRupee, roles: ["committee", "owner", "tenant"], preview: true },
  { to: "/search", label: "Search", icon: Search, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/profile", label: "Profile", icon: User, roles: ["committee", "owner", "tenant", "staff"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["committee"] },
];

const sideLinkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
    isActive ? "bg-[#F4F1EA] text-[#C05640] font-semibold" : "text-stone-700 hover:bg-stone-50"
  }`;

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const role = user?.role || "owner";
  const [community, setCommunity] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    api.get("/communities/mine").then((r) => setCommunity(r.data)).catch((e) => console.error("community load failed", e));
  }, [user?.community_id]);

  // close the More sheet when route changes
  useEffect(() => { setMoreOpen(false); }, [loc.pathname]);

  const navItems = useMemo(() => items.filter((i) => i.roles.includes(role)), [role]);
  const moreLinks = useMemo(() => moreItems.filter((m) => m.roles.includes(role)), [role]);

  const isStaffHome = loc.pathname.startsWith("/staff");
  const signOut = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-900">
      {!isStaffHome && (
        <DesktopSidebar community={community} navItems={navItems} role={role} user={user} onSignOut={signOut} />
      )}

      <main className={isStaffHome ? "" : "md:pl-64 pb-20 md:pb-0"}>{children}</main>

      {!isStaffHome && role !== "staff" && (
        <MobileBottomNav loc={loc} moreOpen={moreOpen} setMoreOpen={setMoreOpen}
          moreLinks={moreLinks} user={user} role={role} onSignOut={signOut} />
      )}
    </div>
  );
}

function DesktopSidebar({ community, navItems, role, user, onSignOut }) {
  const brandName = community?.name || "NivasOne";
  const brandLogo = community?.logo_url;
  return (
    <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 md:w-64 md:bg-white md:border-r md:border-stone-200 md:p-6">
      <Link to="/dashboard" className="flex items-center gap-2 mb-8" data-testid="brand-link">
        {brandLogo ? (
          <img src={brandLogo} alt={brandName} className="w-9 h-9 rounded-xl object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-xl bg-[#C05640] text-white grid place-items-center">
            <Building2 size={20} />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-serif-display text-base font-semibold leading-tight truncate" data-testid="brand-name">{brandName}</div>
          <div className="text-[10px] text-stone-500 tracking-wide">on NivasOne</div>
        </div>
      </Link>
      <nav className="flex-1 space-y-0.5 overflow-y-auto">
        {navItems.map((i) => (
          <NavLink key={i.to} to={i.to} data-testid={`nav-${i.label.toLowerCase()}`} className={sideLinkCls}>
            <i.icon size={18} /> {i.label}
            {i.preview && <span className="ml-auto text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">MOCK</span>}
          </NavLink>
        ))}
        <NavLink to="/profile" data-testid="nav-profile"
          className={(s) => `${sideLinkCls(s)} mt-3`}><User size={18} /> Profile</NavLink>
        {role === "committee" && (
          <NavLink to="/settings" data-testid="nav-settings" className={sideLinkCls}><Settings size={18} /> Settings</NavLink>
        )}
      </nav>
      <div className="border-t border-stone-200 pt-3 mt-3">
        <div className="text-sm font-medium text-stone-900 truncate" data-testid="user-name">{user?.name}</div>
        <div className="text-xs text-stone-500 capitalize">{role}</div>
        <button onClick={onSignOut}
          data-testid="logout-btn"
          className="mt-2 flex items-center gap-2 text-xs text-stone-600 hover:text-[#C05640]">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </aside>
  );
}

function MobileBottomNav({ loc, moreOpen, setMoreOpen, moreLinks, user, role, onSignOut }) {
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 grid grid-cols-5 z-[10000] shadow-[0_-4px_20px_-12px_rgba(0,0,0,0.15)]" style={{paddingBottom: "env(safe-area-inset-bottom)"}}>
      {mobileItems.map((i) => {
        const active = loc.pathname === i.to ||
          (i.to !== "/dashboard" && loc.pathname.startsWith(i.to));
        if (i.primary) {
          return (
            <Link key={i.to} to={i.to} data-testid={`mob-${i.label.toLowerCase()}`}
              className="flex flex-col items-center justify-center -mt-5">
              <span className="w-14 h-14 rounded-full bg-[#C05640] text-white grid place-items-center shadow-lg shadow-[#C05640]/30 active:scale-95 transition">
                <i.icon size={24} />
              </span>
              <span className="text-[10px] text-stone-600 mt-0.5">{i.label}</span>
            </Link>
          );
        }
        return (
          <Link key={i.to} to={i.to} data-testid={`mob-${i.label.toLowerCase()}`}
            className={`flex flex-col items-center justify-center py-3 text-[11px] ${
              active ? "text-[#C05640]" : "text-stone-500"
            }`}>
            <i.icon size={20} /> {i.label}
          </Link>
        );
      })}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetTrigger asChild>
          <button data-testid="mob-more"
            className={`flex flex-col items-center justify-center py-3 text-[11px] ${
              moreOpen ? "text-[#C05640]" : "text-stone-500"
            }`}>
            <MoreHorizontal size={20} /> More
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8" data-testid="more-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="font-serif-display">More</SheetTitle>
          </SheetHeader>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {moreLinks.map((m) => (
              <Link key={m.to} to={m.to} data-testid={`more-${m.label.toLowerCase()}`}
                className="flex flex-col items-center gap-1.5 p-3 rounded-2xl border border-stone-200 hover:border-[#C05640] hover:text-[#C05640] text-stone-700">
                <m.icon size={22} />
                <span className="text-[12px] font-medium">{m.label}{m.preview && <span className="ml-1 text-[8px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded-full font-semibold align-middle">MOCK</span>}</span>
              </Link>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-stone-200 flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm font-medium text-stone-900 truncate">{user?.name}</div>
              <div className="text-xs text-stone-500 capitalize">{role}</div>
            </div>
            <button onClick={onSignOut}
              data-testid="more-logout"
              className="inline-flex items-center gap-1.5 text-xs text-stone-600 hover:text-[#C05640]">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
}
